import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MinimalArticleCard } from '../components/MinimalArticleCard';
import { CategoryList } from '../components/CategoryList';
import { supabase } from '../lib/supabase';
import { IconFallback as Icon } from '../components/IconFallback';
import type { BlogPost } from '../types';

interface BlogProps {
  preview?: boolean;
}

interface CategoryCount {
  category: string;
  count: number;
}

export function Blog({ preview = false }: BlogProps) {
  const [searchParams] = useSearchParams();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<CategoryCount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const activeCategory = searchParams.get('category');

  useEffect(() => {
    fetchPosts();
    if (!preview) {
      fetchCategories();
    }
  }, [preview, activeCategory]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('articles')
        .select('category')
        .eq('published', true);

      if (error) {
        // Silently handle database error and use empty categories
        setCategories([]);
        return;
      }

      const categoryCounts = data.reduce((acc: Record<string, number>, article) => {
        if (article.category) {
          acc[article.category] = (acc[article.category] || 0) + 1;
        }
        return acc;
      }, {});

      setCategories(
        Object.entries(categoryCounts)
          .map(([category, count]) => ({ category, count }))
          .sort((a, b) => b.count - a.count)
      );
    } catch (error) {
      console.warn('Database connection failed:', error);
      setCategories([]);
    }
  };

  const fetchPosts = async () => {
    try {
      setIsLoading(true);
      setError(null);

      let query = supabase
        .from('articles')
        .select(`
          id,
          title,
          slug,
          excerpt,
          content,
          created_at,
          reading_time,
          tags,
          banner_image,
          category
        `)
        .eq('published', true)
        .order('created_at', { ascending: false });

      if (activeCategory) {
        query = query.eq('category', activeCategory);
      }

      if (preview) {
        query = query.limit(2);
      }

      const { data, error } = await query;

      if (error) {
        // Handle database error gracefully
        setPosts([]);
        return;
      }

      setPosts(data.map(post => ({
        id: post.id,
        title: post.title,
        slug: post.slug || '',
        excerpt: post.excerpt || '',
        content: post.content || '',
        date: post.created_at || '',
        readingTime: `${post.reading_time || 5} min read`,
        tags: post.tags || [],
        image: post.banner_image || undefined,
        category: post.category || 'Technology'
      })));
    } catch {
      // Handle database connection failure gracefully
      setPosts([]);
      setError('Failed to load articles');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Icon icon="mdi:loading" className="animate-spin h-8 w-8 text-primary-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500 dark:text-red-400 py-8">
        {error}
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="text-center text-gray-600 dark:text-gray-400 py-8">
        No articles published yet.
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {!preview && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mb-8">
          <div className="lg:col-span-3">
            <motion.div
              layout
              className="space-y-8"
            >
              <AnimatePresence>
                {posts.map((post: BlogPost) => (
                  <MinimalArticleCard 
                    key={post.id} 
                    id={post.id}
                    slug={post.slug}
                    title={post.title}
                    excerpt={post.excerpt}
                    publishedAt={post.date}
                    category={post.category}
                    readingTime={parseInt(post.readingTime.replace(/\D/g, '')) || 5}
                  />
                ))}
              </AnimatePresence>
            </motion.div>
          </div>
          <div className="lg:col-span-1">
            <CategoryList categories={categories} className="sticky top-24" />
          </div>
        </div>
      )}
      {preview && (
        <motion.div
          layout
          className="space-y-8 mb-16"
        >
          <AnimatePresence>
            {posts.map((post: BlogPost) => (
              <MinimalArticleCard 
                key={post.id} 
                id={post.id}
                slug={post.slug}
                title={post.title}
                excerpt={post.excerpt}
                publishedAt={post.date}
                category={post.category}
                readingTime={parseInt(post.readingTime.replace(/\D/g, '')) || 5}
              />
            ))}
          </AnimatePresence>
        </motion.div>
      )}
    </div>
  );
}